/**
 * İstatistik motoru — orkestrasyon.
 *
 * Ayrıştırma → yeniden hesaplama → çelişki tespiti → issue üretimi → skor.
 *
 * Kural: yalnızca confidence ≥ medium olan bulgular "hata" olarak raporlanır.
 * Düşük güvenli bulgular `undecided` olarak işaretlenir ve skora katılmaz.
 */

import type { Issue, ParsedStat, StatsResult } from '../types';
import type { DocumentIndex } from '../text/document';
import { findOperatorSuspects } from '../text/normalize';
import { analyzeStats, findStandalonePs, parseStats, type ParseOptions } from './parser';
import { formatP, recomputeP } from './pvalue';

export interface StatsEngineOutput {
	result: StatsResult;
	issues: Issue[];
}

export function runStatsEngine(
	index: DocumentIndex,
	options: ParseOptions = {}
): StatsEngineOutput {
	const text = index.text;
	const parsed = parseStats(text, index, options);
	const { findings } = analyzeStats(parsed, options);

	const issues: Issue[] = [];
	const push = (issue: Omit<Issue, 'id' | 'decisive'> & { decisive?: boolean }) => {
		issues.push({
			...issue,
			id: `stat-${issues.length + 1}`,
			decisive: issue.decisive ?? issue.confidence !== 'low'
		});
	};

	/* 1) İstatistik başına bulgular -------------------------------------- */

	for (const finding of findings) {
		const { stat } = finding;

		if (finding.severity === 'critical') {
			push({
				code: 'stat.p_threshold_flip',
				category: 'statistics',
				severity: 'critical',
				title: 'Raporlanan p değeri istatistikle çelişiyor',
				detail:
					`${stat.raw}. Bu istatistikten hesaplanan ${formatP(finding.recomputedP ?? Number.NaN)}; ` +
					`raporlanan ${formatP(stat.p ?? Number.NaN)}. İki değer anlamlılık eşiğinin (α = ${options.alpha ?? 0.05}) farklı tarafında. ` +
					'Bu, metin içi bir yazım hatası veya yanlış test istatistiği olabilir; ham veriyle doğrulanmalıdır.',
				quote: stat.context,
				suggestion: 'Test istatistiğini ve serbestlik derecelerini yeniden kontrol edin; p değerini istatistik yazılımından yeniden üretin.',
				location: stat.location,
				confidence: stat.confidence,
				statId: stat.id,
				meta: {
					reportedP: stat.p,
					recomputedP: finding.recomputedP,
					kind: stat.kind,
					value: stat.value,
					df1: stat.df1,
					df2: stat.df2,
					raw: stat.raw,
					alpha: options.alpha ?? 0.05
				}
			});
		} else if (finding.severity === 'warning') {
			push({
				code: 'stat.p_mismatch',
				category: 'statistics',
				severity: 'warning',
				title: 'p değeri istatistikle tam uyuşmuyor',
				detail:
					`${stat.raw}. Raporlanan ${formatP(stat.p ?? Number.NaN)}, istatistikten hesaplanan ${formatP(finding.recomputedP ?? Number.NaN)}. ` +
					'Fark, anlamlılık kararını değiştirmiyor ancak yuvarlama veya yazım hatası olabilir.',
				quote: stat.context,
				suggestion: 'p değerini yeniden hesaplayıp raporlanan değerle eşleştirin.',
				location: stat.location,
				confidence: stat.confidence,
				statId: stat.id,
				meta: { reportedP: stat.p, recomputedP: finding.recomputedP, raw: stat.raw }
			});
		}

		// p hiç raporlanmamış
		if (stat.p === undefined && stat.confidence !== 'low') {
			push({
				code: 'stat.p_missing',
				category: 'statistics',
				severity: 'warning',
				title: 'İstatistik var, p değeri yok',
				detail: `${stat.raw}. APA 7, test istatistiğiyle birlikte p değerinin de raporlanmasını önerir.`,
				quote: stat.context,
				suggestion: `İfadeye ${formatP(finding.recomputedP ?? 0.05)} ekleyin.`,
				location: stat.location,
				confidence: stat.confidence,
				statId: stat.id,
				meta: { raw: stat.raw, suggestedP: finding.recomputedP }
			});
		}

		// df eksik
		if (
			(stat.kind === 't' || stat.kind === 'chi2') &&
			stat.df1 === undefined &&
			stat.confidence !== 'low'
		) {
			push({
				code: 'stat.df_missing',
				category: 'statistics',
				severity: 'info',
				title: 'Serbestlik derecesi belirtilmemiş',
				detail: `${stat.raw}. APA biçimi istatistik adıyla birlikte serbestlik derecesini ister (örn. t(48) = 2.41).`,
				quote: stat.context,
				location: stat.location,
				confidence: stat.confidence,
				statId: stat.id,
				meta: { raw: stat.raw, kind: stat.kind }
			});
		}

		if (stat.kind === 'F' && (stat.df1 === undefined || stat.df2 === undefined)) {
			push({
				code: 'stat.df_missing',
				category: 'statistics',
				severity: 'info',
				title: 'F için serbestlik dereceleri eksik',
				detail: `${stat.raw}. APA biçimi F(df1, df2) = ... bekler.`,
				quote: stat.context,
				location: stat.location,
				confidence: stat.confidence,
				statId: stat.id,
				meta: { raw: stat.raw, kind: 'F' }
			});
		}

		// Ondalık virgül
		if (stat.decimalComma) {
			push({
				code: 'stat.decimal_comma',
				category: 'statistics',
				severity: 'info',
				title: 'Ondalık ayırıcı virgül kullanılmış',
				detail: `${stat.raw}. Türkçe metinlerde kabul edilebilir; ancak dergi stiline göre noktaya çevrilmesi gerekebilir.`,
				quote: stat.context,
				location: stat.location,
				confidence: stat.confidence,
				statId: stat.id,
				meta: { raw: stat.raw }
			});
		}

		// İmkânsız değer
		if (stat.confidence === 'low' && stat.confidenceReasons.some((r) => /aralığı|negatif|dışında/.test(r))) {
			const reason = stat.confidenceReasons.find((r) => /aralığı|negatif|dışında/.test(r)) ?? '';
			push({
				code: 'stat.impossible_value',
				category: 'statistics',
				severity: 'warning',
				title: 'İstatistik değeri olanaksız görünüyor',
				detail: `${stat.raw}. ${reason} Bu genellikle çıkarım hatası veya yazım hatasıdır.`,
				quote: stat.context,
				location: stat.location,
				confidence: 'medium',
				statId: stat.id,
				meta: { raw: stat.raw }
			});
		}
	}

	/* 2) Operatör kaybı --------------------------------------------------- */

	for (const suspect of findOperatorSuspects(text)) {
		const location = index.locationAt(suspect.index, suspect.index + suspect.length);
		const recovered =
			suspect.recoveredOp !== undefined
				? ` Muhtemel doğru ifade: p ${suspect.recoveredOp} ${suspect.pValue?.toFixed(4).replace(/^0/, '') ?? '…'}`
				: '';
		push({
			code: 'stat.operator_corrupt',
			category: 'statistics',
			severity: 'warning',
			title: 'Operatör kaybı şüphesi',
			detail:
				`"${suspect.text}" ifadesinde istatistik operatörünün yerinde virgül/noktalı virgül var. ` +
				`${suspect.reason}${recovered} Bu ifade karar için kullanılmadı.`,
			quote: suspect.text,
			suggestion: 'PDF’teki ilgili cümleyi görsel olarak kontrol edin; operatörü elle düzeltin.',
			location,
			confidence: suspect.confidence,
			meta: {
				index: suspect.index,
				text: suspect.text,
				recoveredOp: suspect.recoveredOp,
				pValue: suspect.pValue
			}
		});
	}

	/* 3) p'siz istatistiksel anlamlılık iddiaları ------------------------- */

	for (const claim of parsed.claims) {
		if (claim.hasP) continue;
		const location = index.locationAt(claim.start, claim.end);
		push({
			code: 'stat.significance_claim',
			category: 'statistics',
			severity: 'warning',
			title: 'Anlamlılık iddiası sayısal destek içermiyor',
			detail: `"${claim.text}" ifadesi geçiyor ancak aynı cümlede raporlanmış bir p değeri yok.`,
			quote: location.line,
			suggestion: 'İlgili teste ait istatistik ve p değerini aynı cümlede raporlayın.',
			location,
			confidence: claim.confidence === 'high' ? 'medium' : 'low',
			decisive: claim.confidence !== 'low',
			meta: { claim: claim.text }
		});
	}

	/* 4) Yalnız p raporlayan ifadeler ------------------------------------- */

	const standalone = findStandalonePs(text).filter(
		(p) => !parsed.stats.some((s) => s.pStart !== undefined && Math.abs(s.pStart - p.start) <= 2)
	);
	for (const p of standalone.slice(0, 5)) {
		const location = index.locationAt(p.start, p.end);
		push({
			code: 'stat.p_missing',
			category: 'statistics',
			severity: 'info',
			title: 'p değeri tek başına raporlanmış',
			detail: `"${p.raw}" ifadesi var ancak hangi test istatistiğine ait olduğu belirtilmemiş.`,
			quote: location.line,
			location,
			confidence: 'medium',
			meta: { raw: p.raw }
		});
	}

	/* Özet + skor ---------------------------------------------------------- */

	const reportable = findings.filter((f) => f.stat.confidence !== 'low');
	const consistent = reportable.filter((f) => f.consistent === true).length;
	const inconsistent = reportable.filter(
		(f) => f.consistent === false && (f.severity === 'critical' || f.severity === 'warning')
	).length;
	const unverifiable =
		findings.length - reportable.length + reportable.filter((f) => f.recomputedP === undefined).length;
	const missingP = reportable.filter((f) => f.stat.p === undefined).length;
	const thresholdFlips = reportable.filter((f) => f.thresholdFlip === true).length;

	const reportedPs = [
		...new Set(
			reportable
				.filter((f) => f.stat.p !== undefined)
				.map((f) => Number((f.stat.p as number).toFixed(4)))
		)
	].sort((a, b) => a - b);

	const score = scoreStats({ inconsistent, thresholdFlips, missingP, total: reportable.length, issues });

	return {
		result: {
			findings,
			claims: parsed.claims.map((c) => ({
				id: `c${c.start}`,
				text: c.text,
				negated: c.negated,
				hasP: c.hasP,
				location: index.locationAt(c.start, c.end),
				confidence: c.confidence
			})),
			reportedPs,
			summary: {
				total: findings.length,
				consistent,
				inconsistent,
				unverifiable,
				missingP,
				thresholdFlips,
				decimalCommas: parsed.decimalCommaCount
			},
			score
		},
		issues
	};
}

/** Aynı p ifadesini iki kez saymamak için mutlak offset karşılaştırması. */
function scoreStats(input: {
	inconsistent: number;
	thresholdFlips: number;
	missingP: number;
	total: number;
	issues: Issue[];
}): number {
	if (input.total === 0) {
		// Hiç istatistik yoksa bu kategori "kontrol edilemedi"; nötr puan.
		return 100;
	}
	let score = 100;
	score -= input.thresholdFlips * 18;
	score -= Math.max(0, input.inconsistent - input.thresholdFlips) * 7;
	score -= (input.missingP / Math.max(1, input.total)) * 20;

	const warnings = input.issues.filter((i) => i.severity === 'warning').length;
	const critical = input.issues.filter((i) => i.severity === 'critical').length;
	score -= warnings * 1.2 + critical * 4;

	return clampScore(score);
}

export function clampScore(v: number): number {
	return Math.max(0, Math.min(100, Math.round(v)));
}

/** Rapor için kısa bir "yeniden hesaplama" özeti. */
export function explainRecompute(stat: ParsedStat): string | null {
	const rec = recomputeP({
		kind: stat.kind as 't' | 'F' | 'chi2' | 'r' | 'z',
		value: stat.value,
		df1: stat.df1,
		df2: stat.df2,
		n: stat.n,
		tails: stat.tails
	});
	return rec.ok ? rec.result.formula : null;
}
